import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WidgetChat } from "./WidgetChat";
import { useWidgetChat } from "@/hooks/useWidgetChat";
import { mediaUrl } from "@/lib/api";
import { initials } from "@/lib/format";
import { X } from "lucide-react";

export function ChatWidget({ apiKey, embed = false, urlIdentity = null, showLauncher = true }) {
  const [open, setOpen] = useState(false);
  const [identity, setIdentity] = useState(urlIdentity);
  const [preview, setPreview] = useState(null);
  const lastPreviewIdRef = useRef(null);
  const previewTimerRef = useRef(null);

  // The chat runs continuously (even while the bubble is closed) so we keep
  // receiving agent replies and can surface an unread badge.
  const chat = useWidgetChat(apiKey, { identity, viewing: open });
  const accent = chat.accent;
  const unread = chat.unreadCount || 0;
  const config = chat.config || {};

  useEffect(() => {
    if (embed && window.parent !== window) {
      window.parent.postMessage({ type: "superchat:state", open }, "*");
    }
  }, [open, embed]);

  // Emit the unread count to the host page so a custom launcher can badge it.
  useEffect(() => {
    if (embed && window.parent !== window) {
      window.parent.postMessage({ type: "superchat:unread", count: unread }, "*");
    }
  }, [unread, embed]);

  // Tell embed.js whether its host-native launcher should open WhatsApp or the
  // SuperChat iframe. The backend supplies a validated wa.me URL only.
  useEffect(() => {
    if (embed && window.parent !== window && chat.config) {
      window.parent.postMessage({
        type: "superchat:launcher-config",
        whatsappUrl: chat.config.whatsapp_redirect_enabled
          ? chat.config.whatsapp_url
          : null,
      }, "*");
    }
  }, [chat.config, embed]);

  // Host page → widget bridge: receive identity (SuperChat.identify) and
  // open/close commands, and announce readiness so queued identity is sent.
  useEffect(() => {
    if (!embed || window.parent === window) return;
    const onMsg = (e) => {
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "superchat:identify" && d.user) {
        // Only STORE the identity — never auto-open. Opening is explicit:
        // the launcher, SuperChat.open()/openWith(), or a data-superchat-open
        // button. (Calling identify() on page load must not pop the widget.)
        setIdentity({
          name: d.user.name, email: d.user.email, phone: d.user.phone, location: d.user.location,
          userId: d.user.userId || d.user.user_id, userHash: d.user.userHash || d.user.user_hash,
        });
      } else if (d.type === "superchat:command") {
        if (d.command === "open") setOpen(true);
        else if (d.command === "close") setOpen(false);
        else if (d.command === "toggle") setOpen((o) => !o);
      }
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ type: "superchat:ready" }, "*");
    return () => window.removeEventListener("message", onMsg);
  }, [embed]);

  // Message preview (Intercom-style): while the bubble is closed, pop the latest
  // agent message as a little card to draw the customer in.
  useEffect(() => {
    if (!showLauncher) {
      setPreview(null);
      return;
    }
    if (open) { setPreview(null); return; }
    const msgs = chat.messages || [];
    const last = msgs[msgs.length - 1];
    if (last && last.sender_type === "agent" && last.id !== lastPreviewIdRef.current) {
      lastPreviewIdRef.current = last.id;
      setPreview(last);
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => setPreview(null), 12000);
    }
  }, [chat.messages, open, showLauncher]);

  useEffect(() => () => clearTimeout(previewTimerRef.current), []);

  // In embed mode, ask embed.js to grow the iframe so the preview card fits.
  useEffect(() => {
    if (embed && window.parent !== window) {
      window.parent.postMessage({ type: "superchat:preview", show: showLauncher && !!preview && !open }, "*");
    }
  }, [preview, open, embed, showLauncher]);

  const previewText = (m) => {
    if (!m) return "";
    if (m.message_type === "image") return "📷 Photo";
    if (m.message_type === "voice") return "🎙️ Voice message";
    if (m.message_type === "file") return "📎 Attachment";
    return m.content || "";
  };

  const PreviewCard = (
    <AnimatePresence>
      {!open && preview && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 360, damping: 28 }}
          onClick={() => { setPreview(null); setOpen(true); }}
          role="button"
          className="font-wa flex w-[300px] max-w-[calc(100vw-24px)] cursor-pointer items-start gap-2.5 rounded-2xl bg-white p-3 text-left shadow-2xl ring-1 ring-black/5"
          data-testid="widget-preview"
        >
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full text-[13px] font-semibold text-white" style={{ background: accent }}>
            {config.widget_avatar
              ? <img src={mediaUrl(config.widget_avatar)} alt="" className="h-full w-full object-cover" />
              : initials(config.display_name || "Support")}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-[#111b18]">{config.display_name || "Support"}</span>
            <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-[#5b6a64]">{previewText(preview)}</span>
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); setPreview(null); }}
            className="-mr-1 -mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[#8a938f] transition-colors hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Headless mode: no floating bubble. The panel fills the iframe and is opened
  // only by the host page's own buttons (SuperChat.open() / data-superchat-open).
  if (!showLauncher) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="font-wa h-full w-full overflow-hidden bg-white"
            data-testid="widget-panel"
          >
            <WidgetChat chat={chat} onClose={() => setOpen(false)} identity={identity} />
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  const Launcher = (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => { setPreview(null); setOpen((o) => !o); }}
        className={`relative flex h-[54px] w-[54px] appearance-none items-center justify-center overflow-visible rounded-full border-0 bg-transparent p-0 text-white outline-none [-webkit-tap-highlight-color:transparent] transition-transform duration-200 hover:scale-[1.06] focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 active:scale-95 ${open ? "shadow-lg" : ""}`}
        style={open ? { background: accent } : undefined}
        data-testid="widget-launcher-button"
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {!open && unread > 0 && (
          <span className="absolute right-0 top-0 z-10 flex h-[19px] min-w-[19px] items-center justify-center rounded-full border-[1.5px] border-white bg-[#ff3b30] px-1 text-[10px] font-bold leading-none text-white shadow-sm" data-testid="widget-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-[23px] w-[23px]" strokeWidth={2.5} aria-hidden="true" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0, scale: 0.6 }} animate={{ rotate: 0, opacity: 1, scale: 1 }} exit={{ rotate: -90, opacity: 0, scale: 0.6 }}>
              <img
                src="/whatsapp-launcher.png"
                alt=""
                className="h-[54px] w-[54px] object-contain"
                aria-hidden="true"
              />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );

  // Embedded (in an iframe sized by embed.js): the open panel FILLS the iframe
  // so there's never an empty box around it. The bubble shows only when closed
  // (the iframe shrinks to bubble-size when closed).
  if (embed) {
    return (
      <>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              className="font-wa fixed inset-0 h-full w-full overflow-hidden bg-white"
              data-testid="widget-panel"
            >
              <WidgetChat chat={chat} onClose={() => setOpen(false)} identity={identity} />
            </motion.div>
          )}
        </AnimatePresence>
        {!open && (
          <div className="fixed bottom-4 right-3 flex flex-col items-end gap-2.5">
            {PreviewCard}
            {Launcher}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="fixed bottom-[68px] right-5 z-[9998] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="font-wa fixed inset-x-3 bottom-24 top-3 h-auto w-auto overflow-hidden rounded-[20px] border border-black/10 bg-white shadow-2xl sm:static sm:inset-auto sm:h-[600px] sm:max-h-[calc(100vh-7rem)] sm:supports-[height:100dvh]:max-h-[calc(100dvh-7rem)] sm:w-[380px]"
            data-testid="widget-panel"
          >
            <WidgetChat chat={chat} onClose={() => setOpen(false)} identity={identity} />
          </motion.div>
        )}
      </AnimatePresence>
      {PreviewCard}
      {Launcher}
    </div>
  );
}
